import * as React from 'react';
import * as classNames from 'classnames';
import { UIEvent, getPageX, getPageY } from '../../uievent';
import { Dimension } from '../../types/common';

export interface ResizableProps {
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  keepAspectRatio: boolean;

  onResize: (width: number, height: number) => void;

  // Called when resizing starts. If `false` is returned explicitly, the resizing will be cancelled.
  onStart?: () => boolean | void;
  onStop?: () => void;
}

interface ResizableState {
  resizing: boolean;
  mousePrevX: number | null;
  mousePrevY: number | null;
}

class Resizable extends React.Component<ResizableProps, ResizableState> {
  private resizeGrip: HTMLElement | null = null;
  private container: HTMLElement | null = null;

  public constructor(props: ResizableProps) {
    super(props);

    this.state = {
      resizing: false,
      mousePrevX: null,
      mousePrevY: null,
    };
  }

  public componentWillUnmount(): void {
    this.stopMouseCapture();
  }

  private stopMouseCapture(): void {
    if (this.container !== null) {
      const { ownerDocument } = this.container;
      if (ownerDocument === null) {
        return;
      }

      ownerDocument.removeEventListener('mousemove', this.handlePointerMove, true);
      ownerDocument.removeEventListener('mouseup', this.handlePointerUp, true);
      ownerDocument.removeEventListener('touchmove', this.handlePointerMove, true);
      ownerDocument.removeEventListener('touchend', this.handlePointerUp, true);
    }
  }

  private startMouseCapture(): void {
    if (this.container === null) {
      throw new Error('Illegal state.');
    }

    const { ownerDocument } = this.container;
    if (ownerDocument === null) {
      return;
    }

    ownerDocument.addEventListener('mousemove', this.handlePointerMove, true);
    ownerDocument.addEventListener('mouseup', this.handlePointerUp, true);
    ownerDocument.addEventListener('touchmove', this.handlePointerMove, true);
    ownerDocument.addEventListener('touchend', this.handlePointerUp, true);
  }

  // ウィンドウからはみ出ないようにサイズを調整する
  // TODO : ウィンドウ自体がリサイズするケースには未対応
  private getMaxSize = (): { width: number, height: number } => {
    if (this.container === null) {
      throw new Error('Illegal state.');
    }

    const parent = document.documentElement; // TODO : Make configurable
    if (parent === null) {
      throw new Error('Illegal state.');
    }

    const parentWidth = parent.clientWidth;
    const parentHeight = parent.clientHeight;

    const { left, top } = this.container.getBoundingClientRect();

    return {
      width: Math.min(parentWidth - left, this.props.maxWidth),
      height: Math.min(parentHeight - top, this.props.maxHeight),
    };
  }

  private handlePointerDown = (ev: UIEvent<HTMLElement>): void => {
    const { onStart } = this.props;
    if (onStart !== undefined) {
      if (onStart() === false) {
        return;
      }
    }

    this.setState({
      resizing: true,
      mousePrevX: getPageX(ev),
      mousePrevY: getPageY(ev),
    });

    this.startMouseCapture();
  }

  private handlePointerMove = (ev: MouseEvent | TouchEvent): void => {
    if (!this.state.resizing) {
      return;
    }

    // iOS Safariにおいて、getBoundingClientRect()の座標がバウンスしたかのような座標になるのを防止
    ev.preventDefault();

    const pageX = ev instanceof MouseEvent ? ev.pageX : ev.changedTouches[0].pageX;
    const pageY = ev instanceof MouseEvent ? ev.pageY : ev.changedTouches[0].pageY;

    let dx = pageX - this.state.mousePrevX!;
    let dy = pageY - this.state.mousePrevY!;

    // 最大/最小サイズ制約などで、グリップとマウスカーソルの位置がずれていく場合がある。
    // 自然な挙動にするため、逆方向に戻したときは、元のグリップ位置に到達するまでリサイズしない。
    const { right: gripX, bottom: gripY } = this.resizeGrip!.getBoundingClientRect();
    if (dx > 0 && pageX < gripX || dx < 0 && pageX > gripX) {
      dx = 0;
    }
    if (dy > 0 && pageY < gripY || dy < 0 && pageY > gripY) {
      dy = 0;
    }

    this.setState({
      mousePrevX: pageX,
      mousePrevY: pageY,
    });

    if (this.props.keepAspectRatio) {
      this.performResizeWithKeepingAspectRatio(dx, dy);
    } else {
      this.performResize(dx, dy);
    }
  }

  private constrainSize = (currentSize: Dimension, newSize: Dimension): Dimension => {
    const { width, height } = currentSize;
    let { width: newWidth, height: newHeight } = newSize;

    const maxSize = this.getMaxSize();
    const maxWidth = Math.min(maxSize.width, this.props.maxWidth);
    const maxHeight = Math.min(maxSize.height, this.props.maxHeight);

    if (newWidth <= this.props.minWidth) {
      newWidth = this.props.minWidth;
      newHeight = height * (this.props.minWidth / width);
    }
    if (newHeight <= this.props.minHeight) {
      newWidth = width * (this.props.minHeight / height);
      newHeight = this.props.minHeight;
    }
    if (maxWidth <= newWidth) {
      newWidth = maxWidth;
      newHeight = height * (maxWidth / width);
    }
    if (maxHeight <= newHeight) {
      newWidth = width * (maxHeight / height);
      newHeight = maxHeight;
    }

    return { width: newWidth, height: newHeight };
  }

  private performResizeWithKeepingAspectRatio = (dx: number, dy: number) => {
    const { width, height } = this.props;

    const transformedWidth = width + dx;
    const transformedHeight = height + dy;

    const horizScale = transformedWidth / width;
    const vertScale = transformedHeight / height;
    const scale = Math.abs(dx) > Math.abs(dy) ? horizScale : vertScale;

    const desiredSize = { width: width * scale, height: height * scale };
    const { width: newWidth, height: newHeight } = this.constrainSize(this.props, desiredSize);

    if (newWidth !== width || newHeight !== height) {
      this.props.onResize(newWidth, newHeight);
    }
  }

  private performResize = (dx: number, dy: number) => {
    const maxSize = this.getMaxSize();

    const width = Math.min(
      Math.max(this.props.minWidth, this.props.width + dx),
      maxSize.width);
    const height = Math.min(
      Math.max(this.props.minHeight, this.props.height + dy),
      maxSize.height);

    if (width !== this.props.width || height !== this.props.height) {
      this.props.onResize(width, height);
    }
  }

  private handlePointerUp = (_ev: MouseEvent | TouchEvent): void => {
    this.stopMouseCapture();

    this.setState({
      resizing: false,
      mousePrevX: null,
      mousePrevY: null,
    });

    const { onStop } = this.props;
    if (onStop !== undefined) {
      onStop();
    }
  }

  public render() {
    const style: React.CSSProperties = {
      position: 'relative',
      width: `${this.props.width}px`,
      height: `${this.props.height}px`,
      overflow: 'hidden',
    };
    const resizeGripStyle: React.CSSProperties = {
      position: 'absolute',
      right: 0,
      bottom: 0,
      cursor: 'nwse-resize',
      userSelect: 'none',
      MozUserSelect: 'none',
    };

    return (
      <div style={style} className={classNames('resizable', { resizing: this.state.resizing })}
        ref={el => this.container = el}>
        {this.props.children}
        <i
          ref={el => this.resizeGrip = el}
          className="material-icons"
          style={resizeGripStyle}
          onMouseDown={this.handlePointerDown}
          onTouchStart={this.handlePointerDown}>
          drag_handle
        </i>
      </div>
    );
  }
}

export default Resizable;
